import { useTranslation } from 'react-i18next';
import { FlowEditor } from './FlowEditor.js';
import { FlowSection, type FlowRetireControls } from './FlowSection.js';
import { FormatHint } from './FormatHint.js';
import type {
  SpecFlow,
  SpecFlowCheckResult,
  SpecFlowInput,
} from '../../../lib/api/spec-requirements.js';

export interface FlowSectionsProps {
  /** 読む形で出す業務フロー。**画面の言語で読んだもの**（訳があれば訳の図） */
  flows: SpecFlow[];
  /** 編集中の図の ID。編集していなければ空文字 */
  editingFlowId: string;
  /**
   * 編集中の図の**原文**。取得中は undefined。
   *
   * 読む形（`flows`）と分けて受けるのが要点である。訳の図をそのまま編集フォームへ渡すと、
   * 保存した瞬間に原文の図が訳で上書きされる。
   */
  source: SpecFlow | undefined;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  saveError: unknown;
  /** 機械チェック中かどうか */
  checking: boolean;
  /** 直前の機械チェックの結果。まだ押していなければ null */
  checkResult: SpecFlowCheckResult | null;
  /** 直前の保存で検知した注意（図ごと） */
  warningsFor: (flowId: string) => string[];
  /** 図を拡大する */
  onZoom: (svg: string) => void;
  /** 保存せずに機械チェックする */
  onCheck: (input: SpecFlowInput) => void;
  /** 編集を始める */
  onEdit: (flowId: string) => void;
  /** 保存する */
  onSave: (flowId: string, input: SpecFlowInput) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** 取り下げ・解除の操作。図ごとに状態（処理中・失敗）を分けたいので関数で受ける */
  retireFor?: (flowId: string) => FlowRetireControls;
  /** レビュー操作。呼び出し元が組み立てる */
  reviewActionsFor: (
    targetId: string,
    label: string,
    version: number,
    review: SpecFlow['review'],
  ) => React.ReactNode;
}

/**
 * 案件の業務フローを並べて出す。
 *
 * 1 本ぶんの見た目は `FlowSection`、編集欄は `FlowEditor` が持つ。ここが持つのは**どの図を
 * 編集中かによる出し分け**だけで、案件 1 件の画面（`SpecRequirementsView`）から切り出してある
 * （画面が成果物ごとの出し分けを全部抱えると 1 ファイルで読めなくなる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の業務フローを並べ、編集中の図だけ原文の編集欄に差し替える
 * @graph-connects FlowSection [calls] 業務フロー 1 本の見た目
 * @graph-connects FlowEditor [calls] 業務フローの編集欄
 * @graph-connects FormatHint [calls] 書き方のルールの表示
 */
export function FlowSections({
  flows,
  editingFlowId,
  source,
  saving,
  saveError,
  checking,
  checkResult,
  warningsFor,
  onZoom,
  onCheck,
  onEdit,
  onSave,
  onCancel,
  retireFor,
  reviewActionsFor,
}: FlowSectionsProps) {
  const { t } = useTranslation();
  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] border-b border-[var(--color-border)] pb-1">
        {t('spec.flow.heading', { n: flows.filter((flow) => flow.retired == null).length })}
      </h2>
      {flows.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.flow.empty')}</p>
      ) : null}
      {flows.map((flow) => (
        <FlowSection
          key={flow.flowId}
          flow={flow}
          onZoom={onZoom}
          warnings={warningsFor(flow.flowId)}
          editor={
            editingFlowId === flow.flowId ? (
              // 原文が揃うまで編集欄を出さない（訳を初期値にしないため）
              source === undefined ? (
                <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
              ) : (
                <FlowEditor
                  flow={source}
                  saving={saving}
                  checking={checking}
                  checkResult={checkResult}
                  error={saveError}
                  onCheck={onCheck}
                  onSave={(input) => {
                    onSave(flow.flowId, input);
                  }}
                  onCancel={onCancel}
                  hint={
                    <FormatHint artifact="business_flow" label={t('spec.artifact.businessFlow')} />
                  }
                />
              )
            ) : null
          }
          onEdit={() => {
            onEdit(flow.flowId);
          }}
          editing={editingFlowId === flow.flowId}
          retire={retireFor?.(flow.flowId)}
          actions={reviewActionsFor(flow.flowId, flow.title, flow.version, flow.review)}
        />
      ))}
    </section>
  );
}
